import { useState, useMemo } from 'react';
import { useTranslate } from '@tolgee/react';
import styled from '@emotion/styled';
import { Modal } from 'antd';
import { useMount } from 'ahooks';
import StatusButton from 'components/StatusButton';
import { useAuthorization } from 'components/Authorization';
import { OnOrder } from 'services/api/pay';
import chatttSDK from 'services/chattt';
import { find } from 'lodash-es';

import CurrencyCheckbox from './CurrencyCheckbox';
import PayTypeCheckbox from './PayTypeCheckbox';
import PackageDetail from './PackageDetail';
import ZhixiaGift from './ZhixiaGift';
import usePayPackage from '../hooks/usePayPackage';
import { EnterpriseTotal } from './EnterpriseTotal';
import { NonEnterpriseTotal, NonEnterpriseTotalProps } from './NonEnterpriseTotal';
import { isQuickChat } from 'productLine';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { CompanyAliHelp, Help } from './OrderAI/Help';
import { AILightWeightServiceOverview } from './AIServiceOverview';

export const canPayAirwallex = () => {
  if (!chatttSDK) {
    return true;
  }

  const {
    app: { isSupport },
  } = chatttSDK;

  return isSupport && isSupport.openWindowWhitelist;
};

export interface PackageProps {
  packageInfo: Pay.PackageInfoV2;
  onOrder: OnOrder;
}

function Package(props: PackageProps) {
  const { packageInfo, onOrder } = props;

  const { t } = useTranslate();

  const {
    isEnterprise,
    currenciesOptions,
    availablePayTypes,
    currency,
    setCurrency,
    payType,
    setPayType,
    setIsEnterprise,
    bank,
    setBank,
  } = usePayPackage();

  const { payload } = useAuthorization<Oversea.Token.Payload>();
  const { accid = '', orgId } = payload || {};

  const language = useSystemLanguageValue();

  const [accountNum, setAccountNum] = useState(0);
  const [durationNum, setDurationNum] = useState<Pay.DurationNum>(0);
  const [canOrder, setCanOrder] = useState(false);
  const [packageId, setPackageId] = useState<Pay.PackageId | undefined>(() => {
    const { recommendData } = packageInfo;
    const { recommendPackageId } = recommendData;
    return recommendPackageId;
  });

  const [calculatePackage, setCalculatePackage] = useState<
    Pay.CalculatePackagePriceRst | undefined
  >();

  const { packageData, recommendData } = packageInfo;
  const { recommendPackageId, recommendDurationId } = recommendData;

  useMount(() => {
    const recommendPackage = find(packageData, ['id', recommendPackageId]);
    if (recommendPackage) {
      setAccountNum(recommendPackage.accountNum);
      const recommendDuration = find(recommendPackage.durationDesc, ['id', recommendDurationId]);
      setDurationNum(recommendDuration?.durationNum ?? 0);
    }
  });

  // 实付价格
  const actualPrice = useMemo(() => calculatePackage?.payPrice ?? 0, [calculatePackage?.payPrice]);

  // 总计价格
  const totalPrices: NonEnterpriseTotalProps['totalPrices'] = useMemo(() => {
    return {
      actualPrice: calculatePackage?.payPrice ?? 0,
      reducePrice: calculatePackage?.reducePrice ?? 0,
    };
  }, [calculatePackage]);

  if (!orgId) {
    return null;
  }

  let orderElement = null;
  if (isEnterprise) {
    // 企业版
    orderElement = <EnterpriseTotal currency={currency} />;
  } else if (canOrder) {
    orderElement = (
      <>
        <NonEnterpriseTotal currency={currency} totalPrices={totalPrices}>
          <ServiceDate>
            {t('market.服务可使用至', { serviceDate: calculatePackage?.afterValidEnd })}
          </ServiceDate>
        </NonEnterpriseTotal>
        <OrderButton
          type="primary"
          size="large"
          onClick={() => {
            if (payType === 'airwallex' && !canPayAirwallex()) {
              Modal.info({
                title: t('market.当前客户端版本不支持 Airwallex 支付'),
                content: t('market.请重启客户端，升级至最新版本来完成订购'),
              });

              return Promise.resolve();
            }

            return onOrder({
              idempotent: `${Date.now()}`,
              accid,
              packageType: isQuickChat ? 3 : 1,
              accountNum,
              durationNum,
              discountTotalPrice: `${actualPrice}`,
              orderAction: 'ORDER',
              payType,
              currency,
              packageId: packageId!,
              language,
              bank,
            });
          }}
        >
          {t('market.立即订购')}
        </OrderButton>
      </>
    );
  }
  return (
    <Container>
      <ZhixiaGift />
      <PackageDetail
        orgId={orgId}
        packageInfo={packageInfo}
        currency={currency}
        isEnterprise={isEnterprise}
        setAccountNum={setAccountNum}
        setDurationNum={setDurationNum}
        setCanOrder={setCanOrder}
        setIsEnterprise={setIsEnterprise}
        onPackageChange={(isEnterprise, select) => {
          if (isEnterprise) {
            setCurrency('CNY');
          }
          setPackageId(select?.id);
        }}
        onCalculatePackageChange={setCalculatePackage}
      />
      <CurrencyCheckbox currencies={currenciesOptions} value={currency} onChange={setCurrency} />
      {isEnterprise ? null : (
        <PayTypeCheckbox
          payTypes={availablePayTypes}
          value={payType}
          onChange={setPayType}
          currency={currency}
          bank={bank}
          onBankChange={setBank}
        />
      )}
      {currency === 'CNY' && (
        <div className="mt-[-12px]">
          <span className="mr-[6px]">
            {t('若需要开具公司抬头增值税发票，请使用公司支付宝进行支付')}
          </span>
          <CompanyAliHelp />
          <Help />
        </div>
      )}
      {orderElement}
      <AILightWeightServiceOverview source="orderPackage" />
    </Container>
  );
}

export default Package;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  > h2 {
    margin-bottom: 12px;
    font-size: 20px;
    color: var(--fett-text-primary);
  }
`;

const OrderButton = styled(StatusButton)`
  width: 260px;
  &,
  &:hover,
  &:active,
  &:focus {
    background-color: var(--fett-app-primary-color);
    border: none;
  }
`;

const ServiceDate = styled.div`
  color: var(--fett-text-tertiary);
`;
